import type { IEvent } from "fabric/fabric-impl";
import { OBJECT_TYPE, OBJECT_MODIFYING_THROTTLE_TIME } from "../constants/canvasConstants";
import { ObjectExtensionFactory, ManageObjectDataFactory, Canvas } from "../types/canvasTypes";
import { ImageOptions, AddImageOptions, ModifyImageObjectPropertiesPayload } from "../types/imageTypes";
import {
  loadImage,
  initControls,
  getProcessedCoordinatesFromStoreForCenteredObject,
  modifyActiveObject,
  getProcessedCoordinatesAndDimensionsFromFabricObject,
} from "../utils/canvasUtils";
import { calculateDefaultOptionsForImage, isImage } from "../utils/imageUtils";
import { findViewportShape } from "../utils/viewportUtils";
import { checkSizeChanging, modifyScaleConsideringSizeRatioLockingIfNeeded } from "../helpers/shapeHelpers";
import { throttle } from "@/lib/utils";

const IMAGE_OBJECT_TYPE = OBJECT_TYPE.image;
const IMAGE_SIZE_CHANGE_EVENT = "image:size-changed";

export type ImageUpdateHandler = (options: Partial<ImageOptions>) => void;

type ImageExtensionOptions = {
  handleOptionsUpdate?: ImageUpdateHandler;
};
export const createImageExtension: ObjectExtensionFactory<
  AddImageOptions,
  ModifyImageObjectPropertiesPayload,
  ImageExtensionOptions
> = (options) => {
  const movingSubscription = createMovingSubscription(options?.handleOptionsUpdate);
  const rotatingSubscription = createRotatingSubscription(options?.handleOptionsUpdate);
  const scalingAndSizeChangeSubscription = createScalingAndSizeChangeSubscription(options?.handleOptionsUpdate);

  return {
    name: IMAGE_OBJECT_TYPE,
    onInit(canvas) {
      movingSubscription.subscribe(canvas);
      rotatingSubscription.subscribe(canvas);
      scalingAndSizeChangeSubscription.subscribe(canvas);
    },
    onDestroy(canvas) {
      movingSubscription.unsubscribe(canvas);
      rotatingSubscription.unsubscribe(canvas);
      scalingAndSizeChangeSubscription.unsubscribe(canvas);
    },
    async onAddObject({ canvas, payload: { imageUrl, isActiveAfterAddition = true } }) {
      const viewPort = findViewportShape(canvas);
      if (!viewPort) return;

      const image = await loadImage(imageUrl);

      const defaultOptions = calculateDefaultOptionsForImage(image, viewPort);
      image.set(defaultOptions);

      initControls(image);

      canvas.add(image);
      if (isActiveAfterAddition) {
        canvas.setActiveObject(image);
      }
      canvas.requestRenderAll();
    },
    onModifyObject({ canvas, payload }) {
      const object = canvas?.getActiveObject();
      if (!object || !isImage(object) || !payload) return;

      const { isSizeRatioLocked, modifiedPropertyName, width, height, ...restOptions } = payload;

      const coordinates = getProcessedCoordinatesFromStoreForCenteredObject({
        top: restOptions.top,
        left: restOptions.left,
        width,
        height,
      });

      const { isWidthChanged, isSizeChanged } = checkSizeChanging(modifiedPropertyName);
      modifyScaleConsideringSizeRatioLockingIfNeeded(object, {
        isSizeRatioLocked,
        isSizeChanged,
        isWidthChanged,
        width,
        height,
      });

      modifyActiveObject(canvas, { ...restOptions, ...coordinates });

      if (isSizeChanged && isSizeRatioLocked) {
        triggerImageSizeChangeEvent(canvas, object);
      }
    },
  };
};

export const createAddImageAction: ManageObjectDataFactory<AddImageOptions> = (payload) => ({
  name: IMAGE_OBJECT_TYPE,
  payload,
});

export const createModifyImageAction: ManageObjectDataFactory<ImageOptions> = (payload) => ({
  name: IMAGE_OBJECT_TYPE,
  payload,
});

type ImageMovingHandler = (coordinates: { top: number; left: number }) => void;
function createMovingSubscription(handler?: ImageMovingHandler) {
  const handleMoving = throttle((event: IEvent<Event>) => {
    const { target } = event;
    if (!target || !isImage(target) || target.left === undefined || target.top === undefined) return;

    const { left, top } = getProcessedCoordinatesAndDimensionsFromFabricObject(target);

    handler?.({
      left,
      top,
    });
  }, OBJECT_MODIFYING_THROTTLE_TIME);

  return {
    subscribe: (canvas: Canvas) => canvas.on("object:moving", handleMoving),
    unsubscribe: (canvas: Canvas) => canvas.off("object:moving", handleMoving),
  };
}

type ImageRotatingHandler = (options: { angle: number }) => void;
function createRotatingSubscription(handler?: ImageRotatingHandler) {
  const handleRotating = throttle((event: IEvent<Event>) => {
    const { target } = event;
    if (!target || !isImage(target) || target.angle === undefined) return;

    handler?.({ angle: Math.trunc(target.angle) });
  }, OBJECT_MODIFYING_THROTTLE_TIME);

  return {
    subscribe: (canvas: Canvas) => canvas.on("object:rotating", handleRotating),
    unsubscribe: (canvas: Canvas) => canvas.off("object:rotating", handleRotating),
  };
}

type ImageScalingAndSizeChangeHandler = (sizes: { width: number; height: number; top: number; left: number }) => void;
function createScalingAndSizeChangeSubscription(handler?: ImageScalingAndSizeChangeHandler) {
  const handleChanges = throttle(({ target }: IEvent<Event>) => {
    if (!target || !isImage(target)) return;

    const options = getProcessedCoordinatesAndDimensionsFromFabricObject(target);

    handler?.(options);
  }, OBJECT_MODIFYING_THROTTLE_TIME);

  return {
    subscribe: (canvas: Canvas) => {
      canvas.on("object:scaling", handleChanges);
      canvas.on(IMAGE_SIZE_CHANGE_EVENT, handleChanges);
    },
    unsubscribe: (canvas: Canvas) => {
      canvas.off("object:scaling", handleChanges);
      canvas.off(IMAGE_SIZE_CHANGE_EVENT, handleChanges);
    },
  };
}

function triggerImageSizeChangeEvent(canvas: Canvas, object: Object) {
  canvas.fire(IMAGE_SIZE_CHANGE_EVENT, { target: object });
}
